/**
 * pushSupport — the DECISIONS behind "Enable alerts on this phone" (Practice Ops web push, plan §5, 2026-11-04).
 *
 * Everything here is pure: the page / service (src/services/pushService.js) reads the browser (user agent, Notification
 * permission, service worker support, localStorage) and hands the facts in. Nothing here touches `window`, `navigator`
 * or a network, so the node-test executes this exact file.
 *
 *   pushState(...)       which of the card's five states to draw:
 *       hidden                 no VAPID key in this build (VUE_APP_FIREBASE_VAPID_KEY absent ⇒ OFF), or the browser cannot do push
 *       ios-needs-home-screen  an iPhone / iPad outside the installed app: iOS delivers web push ONLY to a Home Screen web app
 *                              (iOS 16.4+), so the card shows the Add-to-Home-Screen steps instead of a button
 *       denied                 the user said no (or the phone's settings block Teamio): show how to allow it again
 *       enabled                this device is registered for this employee, and the permission is still granted
 *       can-enable             show the button. Permission is requested ONLY from that tap, never on load.
 *   detectPlatform(...)  'ios' | 'android' | 'desktop' (iPadOS 13+ calls itself a Mac: a touch screen gives it away)
 *   refreshDue(...)      the token is re-checked on page load at most once a day
 *   flag helpers         the per-device, per-employee localStorage flag `teamio_practice_ops_push:<employeeID>`. Storage
 *                        is passed IN (a test passes a fake) and NEVER throws: a read gives "off", a write gives false.
 *   buildSendBody(...)   the POST body of `action: 'send'`, from the ping the page just wrote
 *   skipInPageNotice()   a device that gets the real push does not also show the in-page (Push.js) notice
 *
 * ⛔ A device token is never stored here: the flag keeps only a short fingerprint, to notice a rotation.
 *
 * ⚠️ Keep this file import-free. The node-tests execute this exact file.
 */

export const PUSH_FLAG_PREFIX = 'teamio_practice_ops_push:';
export const STATES = Object.freeze(['hidden', 'ios-needs-home-screen', 'can-enable', 'enabled', 'denied']);
export const DAY_MS = 24 * 60 * 60 * 1000;

const isObj = v => v !== null && typeof v === 'object' && !Array.isArray(v);
const idText = v => (typeof v === 'string' || typeof v === 'number' ? String(v).trim() : '');

/**
 * 'ios' | 'android' | 'desktop' from navigator facts: { userAgent, platform, maxTouchPoints }.
 * iPadOS 13+ reports platform 'MacIntel' with a touch screen; a real Mac has none.
 */
export function detectPlatform(nav) {
  const n = isObj(nav) ? nav : {};
  const ua = typeof n.userAgent === 'string' ? n.userAgent : '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (n.platform === 'MacIntel' && Number(n.maxTouchPoints) > 1) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
}

/** Running as an installed web app: the standalone display mode, or iOS's own navigator.standalone. */
export function isStandalone({ displayModeStandalone, navigatorStandalone } = {}) {
  return displayModeStandalone === true || navigatorStandalone === true;
}

/**
 * The card's state. Facts in, one word out.
 * @param {{configured: boolean, supported: boolean, platform: string, standalone: boolean,
 *          permission: string, enabled: boolean}} f
 *   configured  a VAPID key is in this build        supported  firebase `isSupported()` + serviceWorker + PushManager + Notification
 *   permission  Notification.permission ('default' | 'granted' | 'denied')     enabled  this employee's flag is set on this device
 */
export function pushState(f) {
  const x = isObj(f) ? f : {};
  if (!x.configured) return 'hidden';
  // iOS outside the Home Screen app has no PushManager at all, so "unsupported" must NOT hide the steps there.
  if (x.platform === 'ios' && !x.standalone) return 'ios-needs-home-screen';
  if (!x.supported) return 'hidden';
  if (x.permission === 'denied') return 'denied';
  if (x.enabled && x.permission === 'granted') return 'enabled';
  return 'can-enable';
}

// ── the per-device flag ────────────────────────────────────────────────────────────────────────

/** `teamio_practice_ops_push:<employeeID>`, or null when there is no employee. */
export function flagKey(employeeID) {
  const id = idText(employeeID);
  return id ? `${PUSH_FLAG_PREFIX}${id}` : null;
}

/** A short, non-secret stand-in for a token: its length and last 8 characters. '' for no token. */
export function fingerprint(token) {
  if (typeof token !== 'string' || !token) return '';
  return `${token.length}:${token.slice(-8)}`;
}

/** The stored value: JSON { on, at (ms of the last registration), fp }. */
export function serializeFlag({ atMs, token }) {
  return JSON.stringify({ on: 1, at: Number.isFinite(atMs) ? atMs : 0, fp: fingerprint(token) });
}

/** { on, at, fp } from a stored value; any non-empty value that is not our JSON counts as ON with no history. */
export function parseFlag(raw) {
  if (typeof raw !== 'string' || !raw.trim()) return { on: false, at: null, fp: '' };
  try {
    const v = JSON.parse(raw);
    if (isObj(v)) {
      return { on: v.on === 1 || v.on === true, at: Number.isFinite(v.at) && v.at > 0 ? v.at : null, fp: typeof v.fp === 'string' ? v.fp : '' };
    }
  } catch (e) { /* not JSON: treated as a plain "on" below */ }
  return { on: true, at: null, fp: '' };
}

export function readFlag(storage, employeeID) {
  const key = flagKey(employeeID);
  if (!key || !storage || typeof storage.getItem !== 'function') return { on: false, at: null, fp: '' };
  try {
    return parseFlag(storage.getItem(key));
  } catch (e) {
    return { on: false, at: null, fp: '' };
  }
}

export function writeFlag(storage, employeeID, { atMs, token }) {
  const key = flagKey(employeeID);
  if (!key || !storage || typeof storage.setItem !== 'function') return false;
  try {
    storage.setItem(key, serializeFlag({ atMs, token }));
    return true;
  } catch (e) {
    return false;
  }
}

export function clearFlag(storage, employeeID) {
  const key = flagKey(employeeID);
  if (!key || !storage || typeof storage.removeItem !== 'function') return false;
  try {
    storage.removeItem(key);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Is the once-a-day token refresh due? Only while push is on for this employee on this device; never more than once in 24 h.
 * A last-registration time in the FUTURE (a clock that moved) counts as due, so a bad clock cannot freeze the refresh forever.
 */
export function refreshDue({ enabled, lastAtMs, nowMs }) {
  if (!enabled || !Number.isFinite(nowMs)) return false;
  if (!Number.isFinite(lastAtMs) || lastAtMs <= 0) return true;
  if (lastAtMs > nowMs) return true;
  return nowMs - lastAtMs >= DAY_MS;
}

// ── sending a ping's push ──────────────────────────────────────────────────────────────────────

/** ms of a Date / epoch number / Firestore-like { toMillis } / { seconds }; null for anything else. */
function toMs(v) {
  if (v instanceof Date) return Number.isFinite(v.getTime()) ? v.getTime() : null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (isObj(v) && typeof v.toMillis === 'function') { const ms = v.toMillis(); return Number.isFinite(ms) ? ms : null; }
  if (isObj(v) && Number.isFinite(v.seconds)) return v.seconds * 1000;
  return null;
}

/**
 * The body of POST /practice-pulse-push for the ping the page JUST wrote — ids and time only. The server rebuilds the
 * notification from the board document, so no text, name or note is sent. null when anything needed is missing.
 */
export function buildSendBody(siteID, alert) {
  const site = idText(siteID);
  const a = isObj(alert) ? alert : null;
  if (!site || !a) return null;
  const to = idText(a.pingToEmployeeID);
  const from = idText(a.pingFromEmployeeID);
  const createdAtMs = toMs(a.createdAt);
  const opID = typeof a.opID === 'number' ? a.opID : idText(a.opID);
  if (!to || opID === '' || opID === null || opID === undefined || createdAtMs === null || createdAtMs <= 0) return null;
  const body = { siteID: site, action: 'send', pingToEmployeeID: to, opID, createdAtMs };
  if (from) body.pingFromEmployeeID = from;
  return body;
}

/**
 * Skip the in-page (Push.js) desktop notice for a ping when THIS device already receives the real push for that same
 * person: the push shows it, so the page's own would be a double. The sound still plays (the caller only skips the notice).
 */
export function skipInPageNotice({ pushOnHere, userID, pingToEmployeeID }) {
  const me = idText(userID);
  return pushOnHere === true && me !== '' && me === idText(pingToEmployeeID);
}
